// src/services/costs/providers/estimateTableProvider.js
// Azure Table Storage Provider für Kostenschätzungs-Dossiers (costEstimateItems)
import { importBkpFromCsv } from "../import.js";
import { ensureTableExists } from "../../azureTableUtils";

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "costEstimateItems",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

function safeJsonParse(str, fallback) {
  if (!str) return fallback;
  try { return JSON.parse(str); } catch { return fallback; }
}

function toBool(val) {
  return val === true || val === "true";
}

function bkpRowKey(phase, code) {
  return `${phase}__${code}`;
}

// Apostrophe in OData-Filtern verdoppeln — Phasennamen sind Nutzer:innen-Eingaben.
function escapeOData(value) {
  return String(value).replace(/'/g, "''");
}

// Ein Entity einer Phase als Klon für einen anderen Phasennamen umschreiben.
// Erfasst BKP-Items (RowKey `${phase}__${code}`) und die Kostenkontroll-Meta-Zeile
// (`__ctrlmeta__${phase}`). Andere Zeilen der Partition (z.B. `__col__` Custom
// Columns) sind phasenlos und tauchen im Phase-Filter gar nicht erst auf.
function clonePhaseEntity(entity, targetPhase) {
  const clone = { ...entity };
  delete clone["odata.etag"];
  delete clone.Timestamp;
  if (entity.Code) {
    clone.RowKey = bkpRowKey(targetPhase, entity.Code);
  } else if (String(entity.RowKey).startsWith("__ctrlmeta__")) {
    clone.RowKey = `__ctrlmeta__${targetPhase}`;
  } else {
    return null; // unbekannter Zeilentyp — nicht anfassen
  }
  clone.Phase = targetPhase;
  clone.UpdatedAt = new Date().toISOString();
  return clone;
}

async function queryEntities(filter) {
  let allEntities = [];
  let nextPartitionKey = null;
  let nextRowKey = null;

  do {
    let url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}&${filter}`;

    if (nextPartitionKey || nextRowKey) {
      if (nextPartitionKey) url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
      if (nextRowKey) url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
    }

    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });

    if (!response.ok) {
      if (response.status === 404) break;
      throw new Error(`Azure query error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    allEntities = allEntities.concat(data.value || []);

    nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
    nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
  } while (nextPartitionKey || nextRowKey);

  return allEntities;
}

export class EstimateTableProvider {
  async load(estimateId, phase = "default") {
    try {
      await ensureTableExists(AZURE_CONFIG.tableName);
      // 1. CSV als Basis laden
      const response = await fetch("/bkp.csv");
      const csvText = await response.text();
      const tree = importBkpFromCsv(csvText);

      // 2. Azure-Daten laden (gefiltert nach Phase)
      const filter = `$filter=PartitionKey eq '${estimateId}' and Phase eq '${phase}'`;
      const allEntities = await queryEntities(filter);

      // 3. Azure-Daten über CSV legen
      for (const entity of allEntities) {
        if (!entity.Code) continue;

        tree[entity.Code] = {
          code: entity.Code,
          name: entity.Name,
          phase: entity.Phase || phase,
          source: entity.Source || "",
          length: entity.Length || 0,
          width: entity.Width || 0,
          height: entity.Height || 0,
          quantity: entity.Quantity || 1,
          unit: entity.Unit,
          unitPrice: entity.UnitPrice || 0,
          factor: parseFloat(entity.Factor) || 1,
          totalComputed: entity.TotalComputed || 0,
          // Kostenkontrolle (Phase 41): fixer KV-Wert und vorzeichenbehaftete Erwartung
          kvFixed: parseFloat(entity.KvFixed) || 0,
          erwartung: parseFloat(entity.Erwartung) || 0,
          active: toBool(entity.Active),
          level: entity.Level,
          parentCode: entity.ParentCode || null,
          note: entity.Note || "",
          comment: entity.Comment || "",
          isRisk: toBool(entity.IsRisk),
          isNuessli: toBool(entity.IsNuessli),
          children: safeJsonParse(entity.Children, []),
          isUserAdded: toBool(entity.IsUserAdded),
          tags: safeJsonParse(entity.Tags, []),
          customFields: safeJsonParse(entity.CustomFields, {}),
        };
      }

      // 4. Children-Listen und Level korrigieren
      Object.values(tree).forEach((node) => {
        if (!node.code) return;
        node.children = [];

        if (node.code.includes(".")) {
          const afterDot = node.code.split(".")[1];
          if (afterDot.length === 1) node.level = 4;
          else if (afterDot.length === 2) node.level = 5;
        } else {
          node.level = node.code.length;
        }

        if (node.code.includes(".")) {
          const parts = node.code.split(".");
          const afterDot = parts[1];
          if (afterDot.length === 2) {
            node.parentCode = `${parts[0]}.${afterDot.charAt(0)}`;
          } else if (afterDot.length === 1) {
            node.parentCode = parts[0];
          }
        } else if (node.code.length > 1) {
          node.parentCode = node.code.substring(0, node.code.length - 1);
        } else {
          node.parentCode = null;
        }
      });

      Object.values(tree).forEach((node) => {
        if (!node.code) return;
        if (node.parentCode && tree[node.parentCode]) {
          if (!tree[node.parentCode].children) tree[node.parentCode].children = [];
          if (!tree[node.parentCode].children.includes(node.code)) {
            tree[node.parentCode].children.push(node.code);
          }
        }
      });

      Object.values(tree).forEach((node) => {
        if (!node.phase) node.phase = phase;
      });

      return tree;
    } catch (error) {
      console.error("Load estimate items error:", error);
      throw error;
    }
  }

  async saveBatch(estimateId, ops) {
    try {
      await ensureTableExists(AZURE_CONFIG.tableName);
      const now = new Date().toISOString();

      const BATCH_SIZE = 10;
      const promises = [];

      for (const op of ops) {
        const node = op.node;

        if (op.kind === "delete") {
          promises.push(() => this.deleteEntity(estimateId, node.code, node.phase || "default"));
        } else {
          const entity = {
            PartitionKey: estimateId,
            RowKey: bkpRowKey(node.phase || "default", node.code),
            Code: node.code,
            Name: node.name,
            Source: node.source || "",
            Length: node.length || 0,
            Width: node.width || 0,
            Height: node.height || 0,
            Quantity: node.quantity || 1,
            Unit: node.unit,
            UnitPrice: node.unitPrice || 0,
            Factor: node.factor || 1,
            TotalComputed: node.totalComputed || 0,
            // Kostenkontrolle (Phase 41)
            KvFixed: node.kvFixed || 0,
            Erwartung: node.erwartung || 0,
            Active: node.active || false,
            Level: node.level,
            ParentCode: node.parentCode || "",
            Note: node.note || "",
            Comment: node.comment || "",
            IsRisk: node.isRisk || false,
            IsNuessli: node.isNuessli || false,
            Children: JSON.stringify(node.children || []),
            Tags: JSON.stringify(node.tags || []),
            CustomFields: JSON.stringify(node.customFields || {}),
            Phase: node.phase || "default",
            UpdatedAt: now,
            IsUserAdded: node.isUserAdded || false,
          };

          promises.push(() => this.upsertEntity(entity));
        }
      }

      // Execute in parallel batches
      for (let i = 0; i < promises.length; i += BATCH_SIZE) {
        const batch = promises.slice(i, i + BATCH_SIZE);
        await Promise.all(batch.map((fn) => fn()));
      }
    } catch (error) {
      console.error("Save batch error:", error);
      throw error;
    }
  }

  async upsertEntity(entity) {
    const pk = encodeURIComponent(entity.PartitionKey);
    const rk = encodeURIComponent(entity.RowKey);
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='${pk}',RowKey='${rk}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure upsert error: ${response.status} - ${errorText}`);
    }
  }

  // Insert-or-Merge (MERGE ohne If-Match): setzt nur die übergebenen
  // Properties, alle anderen Felder der Zeile bleiben erhalten.
  async mergeEntity(entity) {
    const pk = encodeURIComponent(entity.PartitionKey);
    const rk = encodeURIComponent(entity.RowKey);
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='${pk}',RowKey='${rk}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "MERGE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure merge error: ${response.status} - ${errorText}`);
    }
  }

  // === Phasen-Verwaltung (US-KOST-019) ===

  // Alle Items einer Phase in eine neue Phase desselben Dossiers kopieren
  // (Datenübernahme beim Erstellen einer Phase). Kopiert auch die
  // Kostenkontroll-Meta-Zeile mit — stammt die Quelle aus einer Phase 41 mit
  // übernommener KV-Basis, gilt das für die Kopie inhaltlich genauso.
  async copyPhaseItems(estimateId, sourcePhase, targetPhase) {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const filter = `$filter=PartitionKey eq '${escapeOData(estimateId)}' and Phase eq '${escapeOData(sourcePhase)}'`;
    const entities = await queryEntities(filter);
    const clones = entities.map((e) => clonePhaseEntity(e, targetPhase)).filter(Boolean);

    const BATCH_SIZE = 10;
    for (let i = 0; i < clones.length; i += BATCH_SIZE) {
      const batch = clones.slice(i, i + BATCH_SIZE);
      await Promise.all(batch.map((c) => this.upsertEntity(c)));
    }
    return clones.length;
  }

  // Alle Items einer Phase auf einen neuen Phasennamen umschreiben (Phase
  // umbenennen). Der Phasenname steckt im RowKey, deshalb copy-then-delete:
  // erst alle Klone unter dem neuen Namen anlegen, dann die alten Zeilen
  // löschen. Bricht der Vorgang ab, existieren Daten schlimmstenfalls doppelt
  // (unter altem UND neuem Namen), gehen aber nie verloren; ein erneuter
  // Versuch ist idempotent (Query nach dem alten Namen findet nur den Rest).
  async renamePhaseItems(estimateId, oldPhase, newPhase) {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const filter = `$filter=PartitionKey eq '${escapeOData(estimateId)}' and Phase eq '${escapeOData(oldPhase)}'`;
    const entities = await queryEntities(filter);
    const pairs = entities
      .map((e) => ({ oldRowKey: e.RowKey, clone: clonePhaseEntity(e, newPhase) }))
      .filter((p) => p.clone);

    const BATCH_SIZE = 10;
    for (let i = 0; i < pairs.length; i += BATCH_SIZE) {
      const batch = pairs.slice(i, i + BATCH_SIZE);
      await Promise.all(batch.map((p) => this.upsertEntity(p.clone)));
    }
    for (let i = 0; i < pairs.length; i += BATCH_SIZE) {
      const batch = pairs.slice(i, i + BATCH_SIZE);
      await Promise.all(batch.map((p) => this.deleteEntityByRowKey(estimateId, p.oldRowKey)));
    }
    return pairs.length;
  }

  // Alle Items einer Phase endgültig löschen (Phase löschen). Erfasst über den
  // Phase-Filter auch die Kostenkontroll-Meta-Zeile. Idempotent — bei einem
  // Abbruch findet ein erneuter Versuch nur die verbliebenen Zeilen.
  async deletePhaseItems(estimateId, phase) {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const filter = `$filter=PartitionKey eq '${escapeOData(estimateId)}' and Phase eq '${escapeOData(phase)}'`;
    const entities = await queryEntities(filter);

    const BATCH_SIZE = 10;
    for (let i = 0; i < entities.length; i += BATCH_SIZE) {
      const batch = entities.slice(i, i + BATCH_SIZE);
      await Promise.all(batch.map((e) => this.deleteEntityByRowKey(estimateId, e.RowKey)));
    }
    return entities.length;
  }

  // === Kostenkontrolle-Meta (KOST-006) ===
  // Ein einzelnes Meta-Entity pro Phase, das festhaelt, ob die KV-Basis bereits
  // aus Phase 32 uebernommen wurde. Bewusst NICHT am costEstimates-Record
  // gefuehrt, weil dessen Service (updateEstimate/getAllEstimates) nur feste
  // Felder round-trippt und ein zusaetzliches Flag beim MERGE ueberschrieben
  // wuerde. RowKey `__ctrlmeta__${phase}`; kein Code-Feld -> von load() ignoriert.
  async loadControlMeta(estimateId, phase) {
    try {
      await ensureTableExists(AZURE_CONFIG.tableName);
      const pk = encodeURIComponent(estimateId);
      const rk = encodeURIComponent(`__ctrlmeta__${phase}`);
      const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='${pk}',RowKey='${rk}')?${AZURE_CONFIG.sasToken}`;
      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });
      if (!response.ok) {
        // 404 = noch keine Meta-Zeile => KV-Basis noch nicht uebernommen
        return { kvBaseImported: false, objektgliederung: null };
      }
      const entity = await response.json();
      return {
        kvBaseImported: toBool(entity.KvBaseImported),
        // null = an der Phase noch nie gesetzt (Aufrufer fällt auf den
        // früheren Dossier-Wert zurück); "" = bewusst geleert.
        objektgliederung:
          typeof entity.Objektgliederung === "string" ? entity.Objektgliederung : null,
      };
    } catch (error) {
      console.error("Load control meta error:", error);
      return { kvBaseImported: false, objektgliederung: null };
    }
  }

  // MERGE statt PUT: die Meta-Zeile trägt auch die Objektgliederung der Phase,
  // die beim Setzen des KV-Basis-Flags erhalten bleiben muss.
  async saveControlMeta(estimateId, phase, meta) {
    await ensureTableExists(AZURE_CONFIG.tableName);
    await this.mergeEntity({
      PartitionKey: estimateId,
      RowKey: `__ctrlmeta__${phase}`,
      Phase: phase,
      KvBaseImported: meta.kvBaseImported || false,
      UpdatedAt: new Date().toISOString(),
    });
  }

  // Objektgliederung der Kostenkontroll-Phase (US-KOST-023, ADR 0064 Nachtrag
  // 2026-09-30): erscheint auf allen Zahlungsanweisungen dieser Phase.
  async saveControlObjektgliederung(estimateId, phase, objektgliederung) {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const value = String(objektgliederung ?? "").trim();
    await this.mergeEntity({
      PartitionKey: estimateId,
      RowKey: `__ctrlmeta__${phase}`,
      Phase: phase,
      Objektgliederung: value,
      UpdatedAt: new Date().toISOString(),
    });
    return value;
  }

  async loadCustomColumns(estimateId) {
    try {
      await ensureTableExists(AZURE_CONFIG.tableName);
      const filter = `$filter=PartitionKey eq '${estimateId}' and RowKey ge '__col__' and RowKey lt '__col__~'`;
      const entities = await queryEntities(filter);
      return entities.map((e) => ({
        id: e.ColumnId,
        name: e.ColumnName,
        type: e.ColumnType || "tags",
        order: e.ColumnOrder || 0,
        hidden: toBool(e.Hidden),
        createdAt: e.CreatedAt,
        updatedAt: e.UpdatedAt,
      }));
    } catch (error) {
      console.error("Load custom columns error:", error);
      return [];
    }
  }

  async saveCustomColumnsBatch(estimateId, ops) {
    const now = new Date().toISOString();
    for (const op of ops) {
      const col = op.column;
      if (op.kind === "delete") {
        const pk = encodeURIComponent(estimateId);
        const rk = encodeURIComponent(`__col__${col.id}`);
        const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='${pk}',RowKey='${rk}')?${AZURE_CONFIG.sasToken}`;
        await fetch(url, { method: "DELETE", headers: { "If-Match": "*" } });
      } else {
        await this.upsertEntity({
          PartitionKey: estimateId,
          RowKey: `__col__${col.id}`,
          ColumnId: col.id,
          ColumnName: col.name,
          ColumnType: col.type || "tags",
          ColumnOrder: col.order || 0,
          Hidden: col.hidden || false,
          CreatedAt: col.createdAt || now,
          UpdatedAt: now,
        });
      }
    }
  }

  // Alle Entities eines Quell-Dossiers (BKP-Items aller Phasen + Custom Columns)
  // unter neuer PartitionKey in die Ziel-Partition kopieren.
  // Bei einem Fehler werden bereits angelegte Entities im Ziel wieder geloescht,
  // damit das Ziel-Dossier nicht in einem halbkopierten Zustand zurueckbleibt.
  async copyAllItems(sourceEstimateId, targetEstimateId) {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const now = new Date().toISOString();
    const filter = `$filter=PartitionKey eq '${sourceEstimateId}'`;
    const entities = await queryEntities(filter);

    // Azure-System-Felder beim Uebertrag entfernen, PartitionKey umschreiben.
    const BATCH_SIZE = 10;
    const upserts = entities.map((e) => {
      const clone = { ...e };
      delete clone["odata.etag"];
      delete clone.Timestamp;
      clone.PartitionKey = targetEstimateId;
      clone.UpdatedAt = now;
      return clone;
    });

    const succeeded = [];
    try {
      for (let i = 0; i < upserts.length; i += BATCH_SIZE) {
        const batch = upserts.slice(i, i + BATCH_SIZE);
        await Promise.all(batch.map((entity) => this.upsertEntity(entity)));
        succeeded.push(...batch);
      }
    } catch (err) {
      // Rollback: alle bereits kopierten Entities aus der Ziel-Partition entfernen.
      // Best-effort - Cleanup-Fehler werden geloggt, der Original-Fehler wird weitergereicht.
      await Promise.allSettled(
        succeeded.map((entity) => this.deleteEntityByRowKey(targetEstimateId, entity.RowKey))
      ).then((results) => {
        const failed = results.filter((r) => r.status === "rejected").length;
        if (failed > 0) {
          console.error(`copyAllItems rollback: ${failed}/${succeeded.length} cleanup-Loeschungen fehlgeschlagen`);
        }
      });
      throw err;
    }

    return upserts.length;
  }

  // Loescht ein einzelnes Entity per (PartitionKey, RowKey) - ohne phase/code-Konstruktion.
  // Wird vom copyAllItems-Rollback genutzt, weil dort die rohen RowKeys vorliegen.
  async deleteEntityByRowKey(estimateId, rowKey) {
    const pk = encodeURIComponent(estimateId);
    const rk = encodeURIComponent(rowKey);
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='${pk}',RowKey='${rk}')?${AZURE_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "DELETE",
      headers: { "If-Match": "*" },
    });
    if (!response.ok && response.status !== 404) {
      throw new Error(`Azure delete error: ${response.status} ${response.statusText}`);
    }
  }

  async deleteEntity(estimateId, code, phase) {
    const rowKey = bkpRowKey(phase, code);
    const pk = encodeURIComponent(estimateId);
    const rk = encodeURIComponent(rowKey);
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='${pk}',RowKey='${rk}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "DELETE",
      headers: { "If-Match": "*" },
    });

    if (!response.ok && response.status !== 404) {
      throw new Error(`Azure delete error: ${response.status}`);
    }
  }
}
